// Complete App.js with Billing, Ucell, CDR, PARS, and Engine Reports tabs
import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Container, Card, CardContent,
  TextField, Button, Grid, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Select, MenuItem, FormControl,
  InputLabel, Box, Alert, CircularProgress, Chip, Tabs, Tab, Divider
} from '@mui/material';
import { Search, Dashboard, ExitToApp, Person, Receipt, Payment, Sms, BarChart, Message } from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart as ReBarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, LineChart, Line
} from 'recharts';
import axios from 'axios';

const API_BASE = 'https://cc-history-checkint.intellic.az/api';

// ------------------------------------------------------------------ //
//  Helpers
// ------------------------------------------------------------------ //

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`main-tabpanel-${index}`}
      aria-labelledby={`main-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const STATUS_COLORS = {
  DELIVERED:     'success',
  UNDELIVERABLE: 'error',
  ENROUTE:       'warning',
  REJECTED:      'error',
  EXPIRED:       'default',
  ACCEPTED:      'info',
  UNKNOWN:       'default',
};

// PARS status map (numeric → label)
const PARS_STATUS_MAP = {
  1: 'ENROUTE',
  2: 'DELIVERED',
  3: 'EXPIRED',
  4: 'DELETED',
  5: 'UNDELIVERABLE',
  6: 'ACCEPTED',
  7: 'UNKNOWN',
  8: 'REJECTED',
};

// ------------------------------------------------------------------ //
//  Billing Charges Tab
// ------------------------------------------------------------------ //

function BillingChargesTab({ user }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchForm, setSearchForm] = useState({
    msisdn: '', startDate: '', endDate: '', state: '', serviceId: ''
  });
  const [summary, setSummary] = useState(null);
  const [charges, setCharges] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ states: [], services: [] });

  useEffect(() => { loadFilterOptions(); }, []);

  const loadFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/charges/filters`);
      setFilterOptions(response.data);
    } catch (error) {
      console.warn('Failed to load filter options:', error);
    }
  };

  const searchCharges = async () => {
    if (!searchForm.msisdn.trim()) { setError('Please enter MSISDN'); return; }
    setLoading(true); setError('');
    try {
      const params = { msisdn: searchForm.msisdn };
      if (searchForm.startDate) params.start_date = searchForm.startDate;
      if (searchForm.endDate)   params.end_date   = searchForm.endDate;
      if (searchForm.state)     params.state       = parseInt(searchForm.state);
      if (searchForm.serviceId) params.service_id  = parseInt(searchForm.serviceId);

      const [summaryResponse, chargesResponse] = await Promise.all([
        axios.get(`${API_BASE}/charges/summary`, { params }),
        axios.get(`${API_BASE}/charges/search`,  { params })
      ]);
      setSummary(summaryResponse.data.summary);
      setCharges(chargesResponse.data.records);
    } catch (error) {
      setError(error.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchForm({ msisdn: '', startDate: '', endDate: '', state: '', serviceId: '' });
    setSummary(null); setCharges([]); setError('');
  };

  return (
    <Box>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
            Billing History Search
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="MSISDN" value={searchForm.msisdn}
                onChange={(e) => setSearchForm({...searchForm, msisdn: e.target.value})}
                placeholder="998933963232" />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="Start Date" value={searchForm.startDate}
                onChange={(e) => setSearchForm({...searchForm, startDate: e.target.value})}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="End Date" value={searchForm.endDate}
                onChange={(e) => setSearchForm({...searchForm, endDate: e.target.value})}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select value={searchForm.state} label="State"
                  onChange={(e) => setSearchForm({...searchForm, state: e.target.value})}>
                  <MenuItem value="">All States</MenuItem>
                  {filterOptions.states.map(state => (
                    <MenuItem key={state} value={state}>{state}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Service</InputLabel>
                <Select value={searchForm.serviceId} label="Service"
                  onChange={(e) => setSearchForm({...searchForm, serviceId: e.target.value})}>
                  <MenuItem value="">All Services</MenuItem>
                  {filterOptions.services.slice(0, 10).map(service => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.id} ({service.count.toLocaleString()})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="contained" startIcon={<Search />}
                  onClick={searchCharges} disabled={loading} fullWidth>
                  Search
                </Button>
                <Button variant="outlined" onClick={clearSearch} fullWidth>Clear</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {summary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { label: 'Transactions', value: summary.total_transactions.toLocaleString(), color: 'primary' },
            { label: 'Total Charged', value: summary.total_charged.toFixed(2), color: 'success.main' },
            { label: 'Avg Amount',    value: summary.avg_charged.toFixed(2),    color: 'warning.main' },
            { label: 'Services',      value: summary.unique_services,            color: 'info.main' },
          ].map(({ label, value, color }) => (
            <Grid item xs={12} md={3} key={label}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" color={color}>{value}</Typography>
                <Typography variant="subtitle2">{label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {charges.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Billing History for {searchForm.msisdn} ({charges.length} records)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell align="center">Service</TableCell>
                    <TableCell align="center">State</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="center">Network</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {charges.map((charge, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{new Date(charge.created_time).toLocaleString()}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>
                        {charge.transaction_id || 'N/A'}
                      </TableCell>
                      <TableCell align="center"><Chip label={charge.service_id} size="small" /></TableCell>
                      <TableCell align="center">
                        <Chip label={charge.state} size="small"
                          color={charge.state === 1 ? 'success' : charge.state === -1 ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {charge.charged_amount ? charge.charged_amount.toFixed(2) : '0.00'}
                      </TableCell>
                      <TableCell align="center">{charge.network_id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

// ------------------------------------------------------------------ //
//  Ucell Charges Tab
// ------------------------------------------------------------------ //

function UcellChargesTab({ user }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchForm, setSearchForm] = useState({
    msisdn: '', startDate: '', endDate: '', chargingStatus: '', serviceId: ''
  });
  const [charges, setCharges] = useState([]);

  const searchUcellCharges = async () => {
    if (!searchForm.msisdn.trim()) { setError('Please enter MSISDN'); return; }
    setLoading(true); setError('');
    try {
      const params = { msisdn: searchForm.msisdn };
      if (searchForm.startDate) params.start_date = searchForm.startDate;
      if (searchForm.endDate)   params.end_date   = searchForm.endDate;

      const response = await axios.get(`${API_BASE}/subscriber/all-charges`, { params });
      setCharges(response.data.ucell_charges.records || []);
    } catch (error) {
      setError(error.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchForm({ msisdn: '', startDate: '', endDate: '', chargingStatus: '', serviceId: '' });
    setCharges([]); setError('');
  };

  return (
    <Box>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Payment sx={{ mr: 1, verticalAlign: 'middle' }} />
            Charge Logs Search
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="MSISDN" value={searchForm.msisdn}
                onChange={(e) => setSearchForm({...searchForm, msisdn: e.target.value})}
                placeholder="998933963232" />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="Start Date" value={searchForm.startDate}
                onChange={(e) => setSearchForm({...searchForm, startDate: e.target.value})}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="End Date" value={searchForm.endDate}
                onChange={(e) => setSearchForm({...searchForm, endDate: e.target.value})}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Charging Status</InputLabel>
                <Select value={searchForm.chargingStatus} label="Charging Status"
                  onChange={(e) => setSearchForm({...searchForm, chargingStatus: e.target.value})}>
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="0">Success (0)</MenuItem>
                  <MenuItem value="1">Failed (1)</MenuItem>
                  <MenuItem value="-1">Error (-1)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Service ID" value={searchForm.serviceId}
                onChange={(e) => setSearchForm({...searchForm, serviceId: e.target.value})}
                placeholder="35" />
            </Grid>
            <Grid item xs={12} md={1}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="contained" startIcon={<Search />}
                  onClick={searchUcellCharges} disabled={loading} fullWidth>
                  Search
                </Button>
                <Button variant="outlined" onClick={clearSearch} fullWidth>Clear</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {charges.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Charge Logs for {searchForm.msisdn} ({charges.length} records)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell align="center">Service</TableCell>
                    <TableCell align="center">Charging Status</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="center">Tariff</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {charges.map((charge, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{new Date(charge.created_time).toLocaleString()}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8em' }}>
                        {charge.transaction_id || 'N/A'}
                      </TableCell>
                      <TableCell align="center"><Chip label={charge.service_id} size="small" /></TableCell>
                      <TableCell align="center">
                        <Chip label={charge.charging_status} size="small"
                          color={charge.charging_status === 0 ? 'success' : 'warning'} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {charge.charged_amount ? charge.charged_amount.toFixed(2) : '0.00'}
                      </TableCell>
                      <TableCell align="center">{charge.tariff_id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {searchForm.msisdn && !loading && charges.length === 0 && (
        <Alert severity="info">No charge logs found for this MSISDN</Alert>
      )}
    </Box>
  );
}

// ------------------------------------------------------------------ //
//  Expandable Text cell for CDR / PARS
// ------------------------------------------------------------------ //

function ExpandableText({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return '—';
  const isLong = text.length > 60;
  return (
    <Box
      onClick={() => isLong && setExpanded(!expanded)}
      sx={{
        cursor: isLong ? 'pointer' : 'default',
        whiteSpace: expanded ? 'normal' : 'nowrap',
        overflow: expanded ? 'visible' : 'hidden',
        textOverflow: expanded ? 'unset' : 'ellipsis',
        maxWidth: expanded ? 'none' : 300,
        fontSize: '0.85em',
        lineHeight: 1.5,
        '&:hover': isLong ? { color: 'primary.main', textDecoration: 'underline dotted' } : {}
      }}
      title={isLong && !expanded ? 'Click to expand' : ''}
    >
      {text}
      {isLong && (
        <Box component="span" sx={{ ml: 0.5, fontSize: '0.75em', color: 'primary.main', fontWeight: 'bold' }}>
          {expanded ? ' [less]' : ' [more]'}
        </Box>
      )}
    </Box>
  );
}

// ------------------------------------------------------------------ //
//  CDR Messages Tab
// ------------------------------------------------------------------ //

function CdrMessagesTab({ user }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchForm, setSearchForm] = useState({
    originator: '', recipient: '', startDate: '', endDate: '', status: ''
  });
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [statuses, setStatuses] = useState([]);

  useEffect(() => { loadStatuses(); }, []);

  const loadStatuses = async () => {
    try {
      const response = await axios.get(`${API_BASE}/cdr/statuses`);
      setStatuses(response.data.statuses);
    } catch (error) {
      console.warn('Failed to load CDR statuses:', error);
    }
  };

  const searchCdr = async () => {
    if (!searchForm.originator.trim() && !searchForm.recipient.trim()) {
      setError('Please enter Originator or Recipient MSISDN');
      return;
    }
    setLoading(true); setError('');
    try {
      const params = {};
      if (searchForm.originator) params.originator  = searchForm.originator;
      if (searchForm.recipient)  params.recipient   = searchForm.recipient;
      if (searchForm.startDate)  params.start_date  = searchForm.startDate;
      if (searchForm.endDate)    params.end_date    = searchForm.endDate;
      if (searchForm.status)     params.status      = searchForm.status;

      const [summaryRes, searchRes] = await Promise.all([
        axios.get(`${API_BASE}/cdr/summary`, { params }),
        axios.get(`${API_BASE}/cdr/search`,  { params })
      ]);

      setSummary(summaryRes.data.summary);
      setRecords(searchRes.data.records);
    } catch (error) {
      setError(error.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchForm({ originator: '', recipient: '', startDate: '', endDate: '', status: '' });
    setSummary(null); setRecords([]); setError('');
  };

  const exportToCsv = () => {
    if (!records.length) return;
    const columns = [
      'id', 'message_time', 'originator', 'recipient',
      'type', 'from_addr', 'to_addr', 'status', 'row_status',
      'delivery_report', 'try_count', 'msg_id', 'virtual_msg_id',
      'mo_unique_id', 'request_id', 'datacoding', 'text'
    ];
    const escape = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n'))
        return `"${str.replace(/"/g, '""')}"`;
      return str;
    };
    const header = columns.join(',');
    const rows = records.map(r => {
      const messageTime = r.sent_time || r.received_time || '';
      return columns.map(col => escape(col === 'message_time' ? messageTime : r[col])).join(',');
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `cdr_${searchForm.originator || searchForm.recipient}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Sms sx={{ mr: 1, verticalAlign: 'middle' }} />
            CDR Message Search
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Originator" value={searchForm.originator}
                onChange={(e) => setSearchForm({...searchForm, originator: e.target.value})}
                placeholder="994501234567" />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Recipient" value={searchForm.recipient}
                onChange={(e) => setSearchForm({...searchForm, recipient: e.target.value})}
                placeholder="994701234567" />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="Start Date" value={searchForm.startDate}
                onChange={(e) => setSearchForm({...searchForm, startDate: e.target.value})}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="End Date" value={searchForm.endDate}
                onChange={(e) => setSearchForm({...searchForm, endDate: e.target.value})}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={searchForm.status} label="Status"
                  onChange={(e) => setSearchForm({...searchForm, status: e.target.value})}>
                  <MenuItem value="">All Statuses</MenuItem>
                  {statuses.map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
                  onClick={searchCdr} disabled={loading} fullWidth>
                  Search
                </Button>
                <Button variant="outlined" onClick={clearSearch} fullWidth>Clear</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {summary && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            { label: 'Total Messages',   value: summary.total_messages,    color: 'primary' },
            { label: 'Delivered',         value: summary.delivered,         color: 'success.main' },
            { label: 'Undeliverable',     value: summary.undeliverable,     color: 'error.main' },
            { label: 'En Route',          value: summary.enroute,           color: 'warning.main' },
            { label: 'Rejected',          value: summary.rejected,          color: 'error.main' },
            { label: 'Unique Recipients', value: summary.unique_recipients, color: 'info.main' },
          ].map(({ label, value, color }) => (
            <Grid item xs={6} md={2} key={label}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" color={color}>{value.toLocaleString()}</Typography>
                <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {records.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">CDR Records ({records.length})</Typography>
              <Button variant="outlined" size="small" onClick={exportToCsv} sx={{ textTransform: 'none' }}>
                ⬇ Export CSV
              </Button>
            </Box>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Message Time</TableCell>
                    <TableCell>Originator</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell align="center">Type</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Row Status</TableCell>
                    <TableCell align="center">Delivery</TableCell>
                    <TableCell align="center">Try Count</TableCell>
                    <TableCell>MSG ID</TableCell>
                    <TableCell>Virtual MSG ID</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell align="center">Datacoding</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Text</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {(() => {
                          const t = r.sent_time || r.received_time;
                          if (!t) return '—';
                          const d = new Date(t);
                          if (d.getFullYear() < 2000) return '—';
                          return d.toLocaleString();
                        })()}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{r.originator || '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{r.recipient  || '—'}</TableCell>
                      <TableCell align="center">
                        {r.type ? <Chip label={r.type} size="small" variant="outlined" /> : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={r.status || '—'} size="small" color={STATUS_COLORS[r.status] || 'default'} />
                      </TableCell>
                      <TableCell align="center">
                        {r.row_status ? <Chip label={r.row_status} size="small" variant="outlined" /> : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={r.delivery_report === 1 ? 'Yes' : 'No'} size="small"
                          color={r.delivery_report === 1 ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell align="center">{r.try_count}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75em', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.msg_id || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75em', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.virtual_msg_id || '—'}
                      </TableCell>
                      <TableCell>{r.from_addr || '—'}</TableCell>
                      <TableCell>{r.to_addr   || '—'}</TableCell>
                      <TableCell align="center">{r.datacoding}</TableCell>
                      <TableCell sx={{ minWidth: 300 }}><ExpandableText text={r.text} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {(searchForm.originator || searchForm.recipient) && !loading && records.length === 0 && (
        <Alert severity="info">No CDR records found for the given search criteria</Alert>
      )}
    </Box>
  );
}

// ------------------------------------------------------------------ //
//  PARS Messages Tab  (NEW)
// ------------------------------------------------------------------ //

function ParsMessagesTab({ user }) {
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [searchForm, setSearchForm] = useState({
    originator: '', recipent: '', startDate: '', endDate: '', status: '', serviceId: ''
  });
  const [summary,  setSummary]  = useState(null);
  const [records,  setRecords]  = useState([]);
  const [statuses, setStatuses] = useState([]);   // [{ value, label }]
  const [services, setServices] = useState([]);   // [{ id, count }]

  useEffect(() => { loadMeta(); }, []);

  const loadMeta = async () => {
    try {
      const [stRes, svRes] = await Promise.all([
        axios.get(`${API_BASE}/pars/statuses`),
        axios.get(`${API_BASE}/pars/services`),
      ]);
      setStatuses(stRes.data.statuses);
      setServices(svRes.data.services);
    } catch (e) {
      console.warn('Failed to load PARS meta:', e);
    }
  };

  const searchPars = async () => {
    if (!searchForm.originator.trim() && !searchForm.recipent.trim()) {
      setError('Please enter Originator or Recipient');
      return;
    }
    setLoading(true); setError('');
    try {
      const params = {};
      if (searchForm.originator) params.originator  = searchForm.originator;
      if (searchForm.recipent)   params.recipent    = searchForm.recipent;
      if (searchForm.startDate)  params.start_date  = searchForm.startDate;
      if (searchForm.endDate)    params.end_date    = searchForm.endDate;
      if (searchForm.status)     params.status      = parseInt(searchForm.status);
      if (searchForm.serviceId)  params.service_id  = parseInt(searchForm.serviceId);

      const [summaryRes, searchRes] = await Promise.all([
        axios.get(`${API_BASE}/pars/summary`, { params }),
        axios.get(`${API_BASE}/pars/search`,  { params }),
      ]);
      setSummary(summaryRes.data.summary);
      setRecords(searchRes.data.records);
    } catch (e) {
      setError(e.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchForm({ originator: '', recipent: '', startDate: '', endDate: '', status: '', serviceId: '' });
    setSummary(null); setRecords([]); setError('');
  };

  const exportToCsv = () => {
    if (!records.length) return;
    const columns = [
      'id', 'message_time', 'originator', 'recipent',
      'service_id', 'network_id', 'sub_service_id',
      'status', 'status_label',
      'msg_id', 'virtual_msg_id', 'replied_to_msg_id', 'text'
    ];
    const escape = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n'))
        return `"${str.replace(/"/g, '""')}"`;
      return str;
    };
    const rows = records.map(r => {
      const messageTime = r.sent_time || r.received_time || '';
      return columns.map(col => escape(col === 'message_time' ? messageTime : r[col])).join(',');
    });
    const csv = [columns.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download',
      `pars_${searchForm.originator || searchForm.recipent}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const parsStatusColor = (statusVal) => {
    const label = PARS_STATUS_MAP[statusVal] || '';
    return STATUS_COLORS[label] || 'default';
  };

  const fmtTime = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    if (d.getFullYear() < 2000) return '—';
    return d.toLocaleString();
  };

  return (
    <Box>
      {/* Search Form */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Message sx={{ mr: 1, verticalAlign: 'middle' }} />
            PARS Message Search
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Originator" value={searchForm.originator}
                onChange={(e) => setSearchForm({...searchForm, originator: e.target.value})}
                placeholder="994501234567" />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label="Recipient" value={searchForm.recipent}
                onChange={(e) => setSearchForm({...searchForm, recipent: e.target.value})}
                placeholder="994701234567" />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="Start Date" value={searchForm.startDate}
                onChange={(e) => setSearchForm({...searchForm, startDate: e.target.value})}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth type="date" label="End Date" value={searchForm.endDate}
                onChange={(e) => setSearchForm({...searchForm, endDate: e.target.value})}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={1}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={searchForm.status} label="Status"
                  onChange={(e) => setSearchForm({...searchForm, status: e.target.value})}>
                  <MenuItem value="">All</MenuItem>
                  {statuses.map(s => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <FormControl fullWidth>
                <InputLabel>Service</InputLabel>
                <Select value={searchForm.serviceId} label="Service"
                  onChange={(e) => setSearchForm({...searchForm, serviceId: e.target.value})}>
                  <MenuItem value="">All</MenuItem>
                  {services.slice(0, 20).map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
                  onClick={searchPars} disabled={loading} fullWidth>
                  Search
                </Button>
                <Button variant="outlined" onClick={clearSearch} fullWidth>Clear</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            { label: 'Total Messages',    value: summary.total_messages,     color: 'primary' },
            { label: 'Delivered',          value: summary.delivered,          color: 'success.main' },
            { label: 'Undeliverable',      value: summary.undeliverable,      color: 'error.main' },
            { label: 'En Route',           value: summary.enroute,            color: 'warning.main' },
            { label: 'Rejected',           value: summary.rejected,           color: 'error.main' },
            { label: 'Unique Services',    value: summary.unique_services,    color: 'info.main' },
          ].map(({ label, value, color }) => (
            <Grid item xs={6} md={2} key={label}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" color={color}>{value.toLocaleString()}</Typography>
                <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Results Table */}
      {records.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">PARS Records ({records.length})</Typography>
              <Button variant="outlined" size="small" onClick={exportToCsv} sx={{ textTransform: 'none' }}>
                ⬇ Export CSV
              </Button>
            </Box>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Message Time</TableCell>
                    <TableCell>Originator</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Service</TableCell>
                    <TableCell align="center">Sub Service</TableCell>
                    <TableCell align="center">Network</TableCell>
                    <TableCell>MSG ID</TableCell>
                    <TableCell>Virtual MSG ID</TableCell>
                    <TableCell>Replied To MSG ID</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Text</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {fmtTime(r.sent_time || r.received_time)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{r.originator || '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{r.recipent   || '—'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={r.status_label || r.status}
                          size="small"
                          color={parsStatusColor(r.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {r.service_id ? <Chip label={r.service_id} size="small" /> : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {r.sub_service_id ? <Chip label={r.sub_service_id} size="small" variant="outlined" /> : '—'}
                      </TableCell>
                      <TableCell align="center">{r.network_id || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75em', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.msg_id || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75em', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.virtual_msg_id || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75em', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.replied_to_msg_id || '—'}
                      </TableCell>
                      <TableCell sx={{ minWidth: 300 }}><ExpandableText text={r.text} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {(searchForm.originator || searchForm.recipent) && !loading && records.length === 0 && (
        <Alert severity="info">No PARS messages found for the given search criteria</Alert>
      )}
    </Box>
  );
}

// ------------------------------------------------------------------ //
//  Engine Reports Tab
// ------------------------------------------------------------------ //

const CHART_COLORS = [
  '#1565c0','#2e7d32','#e65100','#6a1b9a','#00838f',
  '#ad1457','#4527a0','#37474f','#558b2f','#f9a825'
];

function EngineReportsTab({ user }) {
  const today   = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7*24*60*60*1000).toISOString().slice(0, 10);

  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [startDate,   setStartDate]   = useState(weekAgo);
  const [endDate,     setEndDate]     = useState(today);
  const [activeTab,   setActiveTab]   = useState(0);

  const [filters,     setFilters]     = useState({ services: [], campaigns: [], events: [] });
  const [selService,  setSelService]  = useState('');
  const [selCampaign, setSelCampaign] = useState('');
  const [selEvent,    setSelEvent]    = useState('');

  const [summary,    setSummary]    = useState(null);
  const [byService,  setByService]  = useState([]);
  const [byEvent,    setByEvent]    = useState([]);
  const [byCampaign, setByCampaign] = useState([]);

  const [msisdn,      setMsisdn]      = useState('');
  const [msisdnData,  setMsisdnData]  = useState([]);
  const [msisdnLoad,  setMsisdnLoad]  = useState(false);
  const [msisdnError, setMsisdnError] = useState('');

  useEffect(() => { loadFilters(); loadAll(); }, []);

  const loadFilters = async () => {
    try {
      const res = await axios.get(`${API_BASE}/engine/filters`);
      setFilters(res.data);
    } catch (e) { console.warn('Filter load failed', e); }
  };

  const buildParams = () => {
    const p = { start_date: startDate, end_date: endDate };
    if (selService)  p.service_id  = selService;
    if (selCampaign) p.campaign_id = selCampaign;
    if (selEvent)    p.event_id    = selEvent;
    return p;
  };

  const loadAll = async () => {
    setLoading(true); setError('');
    try {
      const params = buildParams();
      const [sumRes, svcRes, evtRes, cmpRes] = await Promise.all([
        axios.get(`${API_BASE}/engine/summary`,           { params }),
        axios.get(`${API_BASE}/engine/daily-by-service`,  { params }),
        axios.get(`${API_BASE}/engine/daily-by-event`,    { params }),
        axios.get(`${API_BASE}/engine/daily-by-campaign`, { params }),
      ]);
      setSummary(sumRes.data);
      setByService(svcRes.data.records);
      setByEvent(evtRes.data.records);
      setByCampaign(cmpRes.data.records);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load reports');
    } finally { setLoading(false); }
  };

  const clearFilters = () => { setSelService(''); setSelCampaign(''); setSelEvent(''); };

  const searchMsisdn = async () => {
    if (!msisdn.trim()) { setMsisdnError('Enter MSISDN'); return; }
    setMsisdnLoad(true); setMsisdnError('');
    try {
      const params = { msisdn, start_date: startDate, end_date: endDate };
      if (selService)  params.service_id  = selService;
      if (selCampaign) params.campaign_id = selCampaign;
      if (selEvent)    params.event_id    = selEvent;
      const res = await axios.get(`${API_BASE}/engine/msisdn-history`, { params });
      setMsisdnData(res.data.records);
    } catch (e) {
      setMsisdnError(e.response?.data?.detail || 'Search failed');
    } finally { setMsisdnLoad(false); }
  };

  const serviceChartData = (() => {
    const byDay = {}; const keys = new Set();
    byService.forEach(r => {
      const k = r.service_name || `SVC_${r.service_id}`;
      if (!byDay[r.day]) byDay[r.day] = { day: r.day };
      byDay[r.day][k] = r.charged_amount; keys.add(k);
    });
    return { data: Object.values(byDay).sort((a,b) => a.day.localeCompare(b.day)), keys: [...keys] };
  })();

  const campaignChartData = (() => {
    const byDay = {}; const keys = new Set();
    byCampaign.forEach(r => {
      const k = r.campaign_name || 'Unknown';
      if (!byDay[r.day]) byDay[r.day] = { day: r.day };
      byDay[r.day][k] = (byDay[r.day][k] || 0) + r.charged_amount; keys.add(k);
    });
    return { data: Object.values(byDay).sort((a,b) => a.day.localeCompare(b.day)), keys: [...keys] };
  })();

  const filteredCampaigns = selService
    ? filters.campaigns.filter(c => String(c.service_id) === String(selService))
    : filters.campaigns;
  const filteredEvents = selCampaign
    ? filters.events.filter(e => String(e.campaign_id) === String(selCampaign))
    : filters.events;

  const SuccessChip = ({ pct }) => (
    <Chip label={`${pct}%`} size="small"
      color={pct >= 90 ? 'success' : pct >= 70 ? 'warning' : 'error'} />
  );

  const ServiceView = () => (
    <Box>
      {serviceChartData.data.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Daily Charged Amount by Service</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <ReBarChart data={serviceChartData.data} margin={{ top:5, right:20, left:10, bottom:5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => v.toLocaleString()} />
                <Legend />
                {serviceChartData.keys.map((k, i) => (
                  <Bar key={k} dataKey={k} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </ReBarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>By Service — Detail</Typography>
          <TableContainer sx={{ maxHeight: 450 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell><TableCell>Service</TableCell>
                  <TableCell align="right">Total</TableCell><TableCell align="right">Success</TableCell>
                  <TableCell align="right">Failed</TableCell><TableCell align="right">Success %</TableCell>
                  <TableCell align="right">Charged</TableCell><TableCell align="right">Debt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byService.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.day}</TableCell>
                    <TableCell><Chip label={r.service_name || r.service_id} size="small" /></TableCell>
                    <TableCell align="right">{r.total_txn.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color:'success.main', fontWeight:'bold' }}>{r.success.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: r.failed>0 ? 'error.main':'inherit' }}>{r.failed.toLocaleString()}</TableCell>
                    <TableCell align="right"><SuccessChip pct={r.success_pct} /></TableCell>
                    <TableCell align="right" sx={{ fontWeight:'bold' }}>{r.charged_amount.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: r.daily_debt>0 ? 'warning.main':'inherit' }}>{r.daily_debt.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  const EventView = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>By Event — Detail</Typography>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell align="center">Event ID</TableCell>
                <TableCell>Campaign</TableCell><TableCell>Service</TableCell>
                <TableCell align="right">Total</TableCell><TableCell align="right">Success</TableCell>
                <TableCell align="right">Failed</TableCell><TableCell align="right">Success %</TableCell>
                <TableCell align="right">Charged</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byEvent.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ whiteSpace:'nowrap' }}>{r.day}</TableCell>
                  <TableCell align="center"><Chip label={r.event_id} size="small" variant="outlined" /></TableCell>
                  <TableCell>{r.campaign_name || '—'}</TableCell>
                  <TableCell>{r.service_name  || '—'}</TableCell>
                  <TableCell align="right">{r.total_txn.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ color:'success.main', fontWeight:'bold' }}>{r.success.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ color: r.failed>0 ? 'error.main':'inherit' }}>{r.failed.toLocaleString()}</TableCell>
                  <TableCell align="right"><SuccessChip pct={r.success_pct} /></TableCell>
                  <TableCell align="right" sx={{ fontWeight:'bold' }}>{r.charged_amount.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const CampaignView = () => (
    <Box>
      {campaignChartData.data.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Daily Charged Amount by Campaign</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <ReBarChart data={campaignChartData.data} margin={{ top:5, right:20, left:10, bottom:5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => v.toLocaleString()} />
                <Legend />
                {campaignChartData.keys.map((k, i) => (
                  <Bar key={k} dataKey={k} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </ReBarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>By Campaign — Detail</Typography>
          <TableContainer sx={{ maxHeight: 450 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell><TableCell>Campaign</TableCell>
                  <TableCell>Service</TableCell><TableCell align="center">Type</TableCell>
                  <TableCell align="right">Total</TableCell><TableCell align="right">Success</TableCell>
                  <TableCell align="right">Failed</TableCell><TableCell align="right">Success %</TableCell>
                  <TableCell align="right">Charged</TableCell><TableCell align="right">Debt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byCampaign.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ whiteSpace:'nowrap' }}>{r.day}</TableCell>
                    <TableCell sx={{ fontWeight:'bold' }}>{r.campaign_name || '—'}</TableCell>
                    <TableCell>{r.service_name || '—'}</TableCell>
                    <TableCell align="center">
                      <Chip label={r.charge_type} size="small"
                        color={r.charge_type==='Subscription'?'primary':'secondary'} />
                    </TableCell>
                    <TableCell align="right">{r.total_txn.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color:'success.main', fontWeight:'bold' }}>{r.success.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: r.failed>0 ? 'error.main':'inherit' }}>{r.failed.toLocaleString()}</TableCell>
                    <TableCell align="right"><SuccessChip pct={r.success_pct} /></TableCell>
                    <TableCell align="right" sx={{ fontWeight:'bold' }}>{r.charged_amount.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: r.daily_debt>0 ? 'warning.main':'inherit' }}>{r.daily_debt.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  const MsisdnView = () => (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>MSISDN Charge History</Typography>
          <Box sx={{ display:'flex', gap:2, flexWrap:'wrap', alignItems:'center' }}>
            <TextField label="MSISDN" value={msisdn}
              onChange={e => setMsisdn(e.target.value)}
              placeholder="994501234567" size="small" sx={{ width: 200 }} />
            <Button variant="contained" onClick={searchMsisdn} disabled={msisdnLoad}
              startIcon={msisdnLoad ? <CircularProgress size={16} color="inherit" /> : <Search />}>
              Search
            </Button>
            {msisdnData.length > 0 && (
              <Button variant="outlined" size="small" onClick={() => {
                const cols = ['charged_time','service_name','campaign_name','event_id',
                  'charge_type','charging_mno_status','charged_amount','daily_debt','general_debt','transaction_id'];
                const esc = v => {
                  if (v===null||v===undefined) return '';
                  const s = String(v);
                  return s.includes(',')||s.includes('"') ? `"${s.replace(/"/g,'""')}"` : s;
                };
                const csv = [cols.join(','),
                  ...msisdnData.map(r => cols.map(c => esc(c==='charge_type'?(r[c]===0?'Subscription':'Oneshot'):r[c])).join(','))
                ].join('\n');
                const blob = new Blob(['\uFEFF'+csv], { type:'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href=url; a.download=`msisdn_${msisdn}_${new Date().toISOString().slice(0,10)}.csv`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}>⬇ Export CSV</Button>
            )}
          </Box>
          {msisdnError && <Alert severity="error" sx={{ mt:2 }}>{msisdnError}</Alert>}
        </CardContent>
      </Card>

      {msisdnData.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>{msisdn} — {msisdnData.length} records</Typography>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Charged Time</TableCell>
                    <TableCell>Service</TableCell><TableCell>Campaign</TableCell>
                    <TableCell align="center">Event</TableCell><TableCell align="center">Type</TableCell>
                    <TableCell align="center">MNO Status</TableCell>
                    <TableCell align="right">Charged</TableCell><TableCell align="right">Daily Debt</TableCell>
                    <TableCell align="right">General Debt</TableCell><TableCell>Transaction ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {msisdnData.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ whiteSpace:'nowrap' }}>{r.charged_time || '—'}</TableCell>
                      <TableCell>{r.service_name  || r.service_id}</TableCell>
                      <TableCell>{r.campaign_name || '—'}</TableCell>
                      <TableCell align="center"><Chip label={r.event_id} size="small" variant="outlined" /></TableCell>
                      <TableCell align="center">
                        <Chip label={r.charge_type===0?'Sub':'Oneshot'} size="small"
                          color={r.charge_type===0?'primary':'secondary'} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={r.charging_mno_status || '—'} size="small"
                          color={String(r.charging_mno_status).startsWith('2001')?'success':'error'} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight:'bold' }}>{r.charged_amount.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: r.daily_debt>0?'warning.main':'inherit' }}>{r.daily_debt.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: r.general_debt>0?'error.main':'inherit' }}>{r.general_debt.toLocaleString()}</TableCell>
                      <TableCell sx={{ fontFamily:'monospace', fontSize:'0.75em' }}>{r.transaction_id || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <BarChart sx={{ mr:1, verticalAlign:'middle' }} />
            Engine Charging Reports
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth type="date" label="Start Date" value={startDate}
                onChange={e => setStartDate(e.target.value)}
                InputLabelProps={{ shrink:true }} size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth type="date" label="End Date" value={endDate}
                onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink:true }} size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Service</InputLabel>
                <Select value={selService} label="Service"
                  onChange={e => { setSelService(e.target.value); setSelCampaign(''); setSelEvent(''); }}>
                  <MenuItem value="">All Services</MenuItem>
                  {filters.services.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.name || s.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Campaign</InputLabel>
                <Select value={selCampaign} label="Campaign"
                  onChange={e => { setSelCampaign(e.target.value); setSelEvent(''); }}>
                  <MenuItem value="">All Campaigns</MenuItem>
                  {filteredCampaigns.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name || c.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Event</InputLabel>
                <Select value={selEvent} label="Event" onChange={e => setSelEvent(e.target.value)}>
                  <MenuItem value="">All Events</MenuItem>
                  {filteredEvents.map(e => (
                    <MenuItem key={e.id} value={e.id}>
                      {e.id} — {e.campaign_name || 'Campaign '+e.campaign_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={{ display:'flex', gap:1 }}>
                <Button variant="contained" onClick={loadAll} disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}>
                  Load
                </Button>
                <Button variant="outlined" onClick={clearFilters} size="small">Clear</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}

      {summary && (
        <Grid container spacing={2} sx={{ mb:3 }}>
          {[
            { label:'Total Txn',         value: summary.total_txn.toLocaleString(),          color:'primary' },
            { label:'Successful',         value: summary.success.toLocaleString(),            color:'success.main' },
            { label:'Failed',             value: summary.failed.toLocaleString(),             color:'error.main' },
            { label:'Success Rate',       value: `${summary.success_pct}%`,                  color: summary.success_pct>=90?'success.main':'warning.main' },
            { label:'Total Charged',      value: summary.total_charged.toLocaleString(),      color:'info.main' },
            { label:'Total Daily Debt',   value: summary.total_daily_debt.toLocaleString(),   color: summary.total_daily_debt>0?'warning.main':'text.secondary' },
            { label:'Unique Subscribers', value: summary.unique_subscribers.toLocaleString(), color:'primary' },
            { label:'Active Services',    value: summary.active_services,                     color:'info.main' },
          ].map(({ label, value, color }) => (
            <Grid item xs={6} md={3} key={label}>
              <Paper sx={{ p:2, textAlign:'center' }}>
                <Typography variant="h5" color={color}>{value}</Typography>
                <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Card sx={{ mb:3 }}>
        <Box sx={{ borderBottom:1, borderColor:'divider' }}>
          <Tabs value={activeTab} onChange={(e,v) => setActiveTab(v)}>
            <Tab label="By Service"     />
            <Tab label="By Event"       />
            <Tab label="By Campaign"    />
            <Tab label="MSISDN History" />
          </Tabs>
        </Box>
      </Card>

      {loading ? (
        <Box sx={{ textAlign:'center', py:8 }}>
          <CircularProgress />
          <Typography sx={{ mt:2 }} color="text.secondary">Loading reports...</Typography>
        </Box>
      ) : (
        <>
          {activeTab === 0 && <ServiceView  />}
          {activeTab === 1 && <EventView    />}
          {activeTab === 2 && <CampaignView />}
          {activeTab === 3 && <MsisdnView   />}
        </>
      )}
    </Box>
  );
}

// ------------------------------------------------------------------ //
//  Main App
// ------------------------------------------------------------------ //

function App() {
  const [token,          setToken]          = useState(localStorage.getItem('token'));
  const [user,           setUser]           = useState(null);
  const [loginData,      setLoginData]      = useState({ username: '', password: '' });
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [mainTab,        setMainTab]        = useState(0);

  useEffect(() => { document.title = 'Customer Care History Dashboard'; }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUserProfile();
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      if (user.permissions?.dashboard_stats) loadDashboardStats();
      if (!user.permissions?.billing_charges && user.permissions?.ucell_charges) setMainTab(0);
    }
  }, [user]);

  const loadUserProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/me`);
      setUser(response.data);
    } catch (error) { logout(); }
  };

  const loadDashboardStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/dashboard/stats`);
      setDashboardStats(response.data);
    } catch (error) { console.error('Failed to load dashboard stats:', error); }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const formData = new FormData();
      formData.append('username', loginData.username);
      formData.append('password', loginData.password);
      const response = await axios.post(`${API_BASE}/auth/login`, formData);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null); setUser(null); setMainTab(0);
    delete axios.defaults.headers.common['Authorization'];
  };

  // Build tab list dynamically from permissions — order matters
  const getMainTabs = () => {
    const tabs = [];
    if (user?.permissions?.billing_charges) tabs.push({ label: 'Billing History',  icon: <Receipt /> });
    if (user?.permissions?.ucell_charges)   tabs.push({ label: 'Charge Logs',      icon: <Payment /> });
    if (user?.permissions?.cdr_messages)    tabs.push({ label: 'CDR Messages',     icon: <Sms /> });
    if (user?.permissions?.pars_messages)   tabs.push({ label: 'PARS Messages',    icon: <Message /> });
    if (user?.permissions?.engine_reports)  tabs.push({ label: 'Engine Reports',   icon: <BarChart /> });
    return tabs;
  };

  // Compute tab index for each permission key given which tabs are visible
  const getTabIndex = (permission) => {
    let index = 0;
    const order = ['billing_charges', 'ucell_charges', 'cdr_messages', 'pars_messages', 'engine_reports'];
    for (const key of order) {
      if (!user?.permissions?.[key]) continue;
      if (key === permission) return index;
      index++;
    }
    return -1;
  };

  // Login Screen
  if (!token) {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
      }}>
        <Card sx={{ maxWidth: 450, width: '100%', mx: 2, boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Dashboard sx={{ fontSize: 56, color: '#1565c0', mb: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Customer Care
              </Typography>
              <Typography variant="h5" component="h2" gutterBottom sx={{ color: '#1565c0' }}>
                History Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Subscriber Billing & Transaction Analytics
              </Typography>
            </Box>
            <form onSubmit={login}>
              <TextField fullWidth label="Username" value={loginData.username}
                onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                margin="normal" required autoFocus />
              <TextField fullWidth type="password" label="Password" value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                margin="normal" required />
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              <Button type="submit" fullWidth variant="contained" size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 2, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const mainTabs = getMainTabs();

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ backgroundColor: '#1565c0' }}>
        <Toolbar>
          <Dashboard sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Customer Care History Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip label={user?.role?.toUpperCase()} color="secondary" size="small" icon={<Person />} />
            <Typography variant="body2">{user?.full_name}</Typography>
            <Button color="inherit" onClick={logout} startIcon={<ExitToApp />}>Sign Out</Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>

        {user?.permissions?.dashboard_stats && dashboardStats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[
              { label: 'Total Records',      value: dashboardStats.total_records.toLocaleString(),      color: 'primary' },
              { label: 'Unique Subscribers', value: dashboardStats.unique_subscribers.toLocaleString(), color: 'success.main' },
              { label: 'Active Days',         value: dashboardStats.active_days,                         color: 'info.main' },
            ].map(({ label, value, color }) => (
              <Grid item xs={12} sm={6} md={4} key={label}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color={color}>{value}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {mainTabs.length > 1 && (
          <Card sx={{ mb: 4 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={mainTab} onChange={(e, newValue) => setMainTab(newValue)}
                variant="fullWidth" indicatorColor="primary" textColor="primary">
                {mainTabs.map((tab, index) => (
                  <Tab key={index} icon={tab.icon} label={tab.label}
                    iconPosition="start" sx={{ minHeight: 72 }} />
                ))}
              </Tabs>
            </Box>
          </Card>
        )}

        {user?.permissions?.billing_charges && (
          <TabPanel value={mainTab} index={getTabIndex('billing_charges')}>
            <BillingChargesTab user={user} />
          </TabPanel>
        )}

        {user?.permissions?.ucell_charges && (
          <TabPanel value={mainTab} index={getTabIndex('ucell_charges')}>
            <UcellChargesTab user={user} />
          </TabPanel>
        )}

        {user?.permissions?.cdr_messages && (
          <TabPanel value={mainTab} index={getTabIndex('cdr_messages')}>
            <CdrMessagesTab user={user} />
          </TabPanel>
        )}

        {user?.permissions?.pars_messages && (
          <TabPanel value={mainTab} index={getTabIndex('pars_messages')}>
            <ParsMessagesTab user={user} />
          </TabPanel>
        )}

        {user?.permissions?.engine_reports && (
          <TabPanel value={mainTab} index={getTabIndex('engine_reports')}>
            <EngineReportsTab user={user} />
          </TabPanel>
        )}

        {!user?.permissions?.billing_charges &&
         !user?.permissions?.ucell_charges &&
         !user?.permissions?.cdr_messages &&
         !user?.permissions?.pars_messages &&
         !user?.permissions?.engine_reports && (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Access Restricted
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You don't have permission to view any data. Contact your administrator.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
}

export default App;
